import { enums, Infer, number, optional, string, type } from "superstruct";
import { isStruct } from "../utils/isStruct";
import { TimestampStruct } from "./Timestamp";
import { WebhookPayloadEventTypeStruct } from "./WebhookPayload";

export const WebhookDeliveryStatusStruct = enums(["SUCCESS", "FAILED"]);

export type WebhookDeliveryStatus = Infer<typeof WebhookDeliveryStatusStruct>;

export const WebhookDeliveryLogStruct = type({
  id: string(),
  organizationId: string(),
  projectId: string(),
  callId: string(),
  webhookConfigId: string(),
  /**
   * The URL that was called.
   */
  webhookUrl: string(),
  /**
   * The webhook event type for this delivery attempt (initial analysis vs.
   * a regenerated update). Optional for backward compatibility with log
   * entries created before this field existed.
   */
  eventType: optional(WebhookPayloadEventTypeStruct),
  /**
   * Delivery status.
   */
  status: WebhookDeliveryStatusStruct,
  /**
   * HTTP status code returned by the webhook endpoint.
   */
  httpStatusCode: optional(number()),
  /**
   * Error message if delivery failed.
   */
  errorMessage: optional(string()),
  /**
   * Response body snippet (first 500 chars) for debugging.
   */
  responseBodySnippet: optional(string()),
  /**
   * Duration of the HTTP request in milliseconds.
   */
  durationMs: optional(number()),
  createdAt: TimestampStruct,
});

/**
 * Log entry for a webhook delivery attempt.
 * @interface
 * @category Webhook
 */
export type WebhookDeliveryLog = Infer<typeof WebhookDeliveryLogStruct>;

export function isWebhookDeliveryLog(obj: unknown): obj is WebhookDeliveryLog {
  return isStruct(obj, WebhookDeliveryLogStruct, "WebhookDeliveryLog");
}
